// File: frontend/src/components/layout/MainLayout/MainLayout.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { Zap, Menu, X } from 'lucide-react';
import styles from './MainLayout.module.css';

const MainLayout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/compare', label: 'Compare' },
    { path: '/about', label: 'How It Works' }
  ];

  return (
    <div className={styles.mainLayout}>
      <header className={styles.header}>
        <nav className={styles.nav}>
          {/* Logo */}
          <Link to="/" className={styles.logo}>
            <div className={styles.logoIcon}>
              <Zap />
            </div>
            <span className={styles.logoText}>Cover Crush</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className={styles.navLinks}>
            {navItems.map((item) => (
              <Link 
                key={item.path}
                to={item.path} 
                className={`${styles.navLink} ${isActive(item.path) ? styles.active : ''}`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className={styles.navActions}>
            <Link to="/analysis" className={styles.ctaButton}>
              Get My Grade
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className={styles.mobileMenuButton} 
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={styles.mobileMenu}>
            <div className={styles.mobileMenuContent}>
              {navItems.map((item) => (
                <Link 
                  key={item.path}
                  to={item.path} 
                  className={`${styles.mobileNavLink} ${isActive(item.path) ? styles.active : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              <Link 
                to="/analysis" 
                className={styles.mobileCTA}
                onClick={() => setMobileMenuOpen(false)}
              >
                Get My Grade
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className={styles.main}>
        {children}
      </main>

      {/* Simplified Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <div className={styles.logo}>
              <div className={styles.logoIcon}>
                <Zap />
              </div>
              <span className={styles.logoText}>Cover Crush</span>
            </div>
            <p className={styles.footerDescription}>
              Crushing overpriced insurance, one policy at a time.
            </p>
          </div>
          
          <div className={styles.footerLinks}>
            <div className={styles.linkGroup}>
              <h4>Product</h4>
              <Link to="/about">How It Works</Link>
              <Link to="/compare">Compare Rates</Link>
              <Link to="/examples">Examples</Link>
            </div>
            
            <div className={styles.linkGroup}>
              <h4>Legal</h4>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Service</Link>
              <Link to="/security">Security</Link>
            </div>
            
            <div className={styles.linkGroup}>
              <h4>Support</h4>
              <Link to="/help">Help Center</Link>
              <Link to="/contact">Contact Us</Link>
              <Link to="/feedback">Feedback</Link>
            </div>
          </div>
        </div>
        
        <div className={styles.footerBottom}>
          <p>&copy; 2025 Cover Crush. All rights reserved.</p>
          <div className={styles.liveStats}>
            <span className={styles.liveIndicator}>🟢</span>
            <span>1,247 users crushing rates right now</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

MainLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MainLayout;